import { MODULE_ACCESS, SUBSCREEN_ACCESS } from '@mnp/shared';
import type { Role } from './role.ts';

// Access-матрица (кто какой модуль видит/правит) живёт в
// packages/shared/src/rbac (MODULE_ACCESS/SUBSCREEN_ACCESS) — единый источник
// с route-permissions API (карточка rbac-single-source). Здесь остаются только
// UI-метаданные: лейблы, иконки, slug'и, группировка, порядок.
export type { ModuleAccess } from '@mnp/shared';
import type { ModuleAccess } from '@mnp/shared';

export type ModuleGroup = 'main' | 'operations' | 'management' | 'support';

export const MODULE_GROUP_LABELS: Record<ModuleGroup, string | null> = {
  main: null,
  operations: 'Операции',
  management: 'Управление',
  support: 'Поддержка',
};

export interface ModuleConfig {
  id: string;
  slug: string;
  label: string;
  description?: string;
  iconKey: string;
  group: ModuleGroup;
  soon?: boolean;
  access: Record<Role, ModuleAccess>;
  subscreens?: SubscreenConfig[];
}

export interface SubscreenConfig {
  id: string;
  slug: string;
  label: string;
  access: Record<Role, ModuleAccess>;
}

export const MODULES: ModuleConfig[] = [
  {
    id: 'hub',
    slug: '',
    label: 'Рабочий стол',
    iconKey: 'dashboard',
    group: 'main',
    access: MODULE_ACCESS.hub,
  },
  {
    id: 'reception',
    slug: 'reception',
    label: 'Ресепшен',
    iconKey: 'reception',
    group: 'operations',
    access: MODULE_ACCESS.reception,
    subscreens: [
      {
        // Personal-QR client scanner → client card → orders. Renamed from
        // «Выдача» — this is the QR scanner operators use to find a client.
        id: 'delivery',
        slug: 'delivery',
        label: 'Сканер QR',
        access: SUBSCREEN_ACCESS['reception.delivery'],
      },
      {
        id: 'cash',
        slug: 'cash',
        label: 'Касса',
        access: SUBSCREEN_ACCESS['reception.cash'],
      },
      {
        // Забор · «Заборы» (2026-06-05): курьерские заборы Flex-заявок,
        // отсортированы по дате забора (ближайшие сверху). Координируют
        // ресепшен (chats) и склад (fulfillment).
        id: 'pickups',
        slug: 'pickups',
        label: 'Заборы',
        access: SUBSCREEN_ACCESS['reception.pickups'],
      },
      // «Сканер QR (Ozon/WB)» (id: qr) удалён 2026-06-12 вместе с модулем
      // Ozon/WB — архив в ветке archive/ozonwb-flow (тег pre-ozonwb-extract).
      // «Сканер QR» (delivery) выше — другой, живой персональный QR-сканер.
      {
        id: 'couriers',
        slug: 'couriers',
        label: 'Курьеры',
        access: SUBSCREEN_ACCESS['reception.couriers'],
      },
      {
        id: 'kb-shortcut',
        slug: 'kb-shortcut',
        label: 'База знаний',
        access: SUBSCREEN_ACCESS['reception.kb-shortcut'],
      },
    ],
  },
  {
    id: 'orders',
    slug: 'orders',
    label: 'Заказы',
    iconKey: 'orders',
    group: 'operations',
    access: MODULE_ACCESS.orders,
    subscreens: [
      {
        id: 'all',
        slug: 'all',
        label: 'Все заказы',
        access: SUBSCREEN_ACCESS['orders.all'],
      },
      // Сабскрин «Доставка» (id: deliver, Ozon/WB-сканер выдачи) удалён
      // 2026-06-12 — архив в ветке archive/ozonwb-flow (тег pre-ozonwb-extract).
    ],
  },
  // Модуль «Отгрузки» (id: shipments, Ozon/WB-партии) удалён 2026-06-12 —
  // архив в ветке archive/ozonwb-flow (тег pre-ozonwb-extract).
  {
    id: 'payments',
    slug: 'payments',
    label: 'Платежи',
    iconKey: 'money',
    group: 'operations',
    soon: true,
    access: MODULE_ACCESS.payments,
  },
  {
    id: 'accounts',
    slug: 'accounts',
    label: 'Счета',
    iconKey: 'accounts',
    group: 'management',
    soon: true,
    access: MODULE_ACCESS.accounts,
  },
  {
    id: 'cash',
    slug: 'reception/cash',
    label: 'Касса',
    iconKey: 'money',
    group: 'management',
    access: MODULE_ACCESS.cash,
  },
  {
    id: 'clients',
    slug: 'clients',
    label: 'Клиенты',
    iconKey: 'users',
    group: 'management',
    access: MODULE_ACCESS.clients,
  },
  {
    id: 'kb',
    slug: 'kb',
    label: 'База знаний',
    iconKey: 'kb',
    group: 'support',
    soon: true,
    access: MODULE_ACCESS.kb,
  },
  {
    id: 'tasks',
    slug: 'tasks',
    label: 'Задачи',
    iconKey: 'tasks',
    group: 'support',
    soon: true,
    access: MODULE_ACCESS.tasks,
  },
  {
    id: 'warehouse',
    slug: 'warehouse',
    label: 'Склад',
    iconKey: 'warehouse',
    group: 'operations',
    soon: true,
    access: MODULE_ACCESS.warehouse,
  },
  // Ozon/WB-модули «Сверка приёмки РФ» (intake-review), «Тбилиси — приёмка»
  // (intake-tbilisi) и «Россия — сортировка» (sorting-ru) удалены 2026-06-12 —
  // архив в ветке archive/ozonwb-flow (тег pre-ozonwb-extract).
];
